<script setup lang="ts">
import CurrentLanguage from '@/components/CurrentLanguage.vue';
import StoryForm from '@/components/stories/StoryForm.vue';
import StoryItemForm from '@/components/stories/StoryItemForm.vue';
import { Story } from '@/models/story.model';
import { Actions, Mutations, ShiftCMSState } from '@/store';
import { StoriesActions, StoriesState } from '@/stores/stories.store';
import { computed, markRaw, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import {
  TYPE_INFOGRAPHIC,
  TYPE_INFO,
  TYPE_HERO,
  TYPE_QUESTION,
  TYPE_ACTIONS_INITIATIVES,
} from '@/util/constants';
import { StoryItem } from '@/models/story-item.model';
import { useEvents, Event } from '@/services/events.composable';
import { capitalize } from '@/util/string.util';
import { LabelsActions } from '@/stores/labels.store';

const store = useStore<ShiftCMSState & { ts: StoriesState }>();
const route = useRoute();
const router = useRouter();
const events = useEvents();

const story = computed<Story | null>(() => store.state.ts.story);

const drag = ref<boolean>(false);
const dragItem = ref<StoryItem | null>(null);

const editStory = () => {
  store.commit(Mutations.ShowDrawer, {
    component: markRaw(StoryForm),
    data: story.value,
  });
};

const updateLanguage = async () => {
  store.dispatch(StoriesActions.GetStory, route.params.id);
};

onMounted(() => {
  store.dispatch(LabelsActions.GetLabels);
  events.on(Event.LanguageChanged, updateLanguage);
  events.on(Event.DrawerClosed, updateLanguage);
  if (!story.value) {
    store.dispatch(StoriesActions.GetStory, route.params.id);
  }
});

onBeforeUnmount(() => {
  events.off(Event.LanguageChanged, updateLanguage);
  events.off(Event.DrawerClosed, updateLanguage);
});

const addItem = (type: string) => {
  store.commit(Mutations.ShowDrawer, {
    component: markRaw(StoryItemForm),
    data: { story: story.value, type },
  });
};

const editItem = (type: string, item: StoryItem) => {
  store.commit(Mutations.ShowDrawer, {
    component: markRaw(StoryItemForm),
    data: { story: story.value, type, value: item },
  });
};

const removeItem = (event: MouseEvent, story: Story, item: StoryItem) => {
  event.stopPropagation();
  store.dispatch(StoriesActions.RemoveStoryItem, { ...item, story });
};

const togglePublished = async () => {
  if (story.value) {
    await store.dispatch(StoriesActions.UpdateStory, {
      ...story.value,
      published: !story.value.published,
    });

    store.dispatch(Actions.ShowNotification, {
      message: story.value.published
        ? 'Story is no longer published'
        : 'Story is published',
      type: story.value.published ? 'info' : 'success',
    });
  }
};

const removeStory = () => {
  if (
    story.value &&
    confirm('Are you absolutely sure you want to delete this story?')
  ) {
    store.dispatch(StoriesActions.DeleteStory, story.value.id);
    router.push('/stories');
  }
};
const getTitle = (item: StoryItem): string => {
  return item.info
    ? item.info.title
    : item.infoGraphic
    ? item.infoGraphic.title
    : item.hero
    ? item.hero.name
    : item.question
    ? item.question.title
    : item.initiatives?.length || item.actions?.length
    ? 'Actions/Initiativis'
    : '';
};

const getDescription = (item: StoryItem): string => {
  return item.info
    ? item.info.description
    : item.infoGraphic
    ? item.infoGraphic.title
    : item.hero
    ? item.hero.name
    : item.question
    ? item.question.title
    : item.initiatives?.length || item.actions?.length
    ? ''
    : '';
};
const getType = (item: StoryItem): string => {
  return item.info
    ? TYPE_INFO
    : item.infoGraphic
    ? TYPE_INFOGRAPHIC
    : item.hero
    ? TYPE_HERO
    : item.question
    ? TYPE_QUESTION
    : item.actions?.length || item.initiatives?.length
    ? TYPE_ACTIONS_INITIATIVES
    : '';
};

const dragStart = (evt: DragEvent, item: StoryItem) => {
  drag.value = true;
  dragItem.value = item;
  evt.dataTransfer?.setData('text', JSON.stringify(item));
};
const dragEnd = (evt: DragEvent) => {
  evt.preventDefault();
  drag.value = false;
  dragItem.value = null;
};

const dragDrop = (evt: DragEvent, column: number, row: number) => {
  evt.preventDefault();
  drag.value = false;
  if (story.value) {
    store.dispatch(StoriesActions.UpdateStoryItemStructure, {
      ...dragItem.value,
      column,
      row,
      story: story.value,
    });
  }
};

const dragOver = (evt: DragEvent) => {
  evt.preventDefault();

  if (evt.target) {
    (evt.target as Element).classList.add('drag-active');
  }
};
const dragLeave = (evt: DragEvent) => {
  evt.preventDefault();
  if (evt.target) {
    (evt.target as Element).classList.remove('drag-active');
  }
};

const getColumns = (items: StoryItem[]): number[] => {
  return items.reduce((acc: number[], item: StoryItem) => {
    return acc.includes(item.column) ? acc : [...acc, item.column].sort();
  }, []);
};
const getColumnItems = (items: StoryItem[], column: number): StoryItem[] => {
  return items
    .filter((item) => item.column === column)
    .sort((a, b) => a.row - b.row);
};
</script>
<template>
  <div v-if="story" class="page-container">
    <h2>{{ story.title }}<CurrentLanguage /></h2>
    <p>This story contains {{ story.items.length }} items.</p>
    <v-table class="table-container">
      <v-row class="story-container">
        <v-col v-for="column in getColumns(story.items)" :key="column">
          <div
            class="draggable-container"
            v-for="element in getColumnItems(story.items, column)"
            :key="element.id"
          >
            <v-card
              draggable="true"
              @dragstart="(e: DragEvent) => dragStart(e, element)"
              @dragend="(e: DragEvent) => dragEnd(e)"
              elevation="2"
              class="story-item"
              @click="() => editItem(getType(element), element)"
            >
              <v-card-text>
                <h4>{{ getTitle(element) }}</h4>
                <p v-if="!getTitle(element)">{{ getDescription(element) }}</p>
                <p v-if="getType(element) === TYPE_ACTIONS_INITIATIVES">
                  Choose from
                  <span
                    v-text="[...element.actions, ...element.initiatives].length"
                  ></span>
                  choices
                </p>
                <p class="story-item-type">
                  {{ capitalize(getType(element)) }}
                </p>
                <v-btn
                  icon="mdi-delete-outline"
                  class="remove-button"
                  density="compact"
                  @click.prevent="removeItem($event, story, element)"
                ></v-btn>
              </v-card-text>
            </v-card>
            <div
              v-if="
                drag &&
                dragItem &&
                dragItem.id !== element.id &&
                element.column === 0
              "
              class="drop-target drop-before"
              @drop="
                (e) => dragDrop(e, Math.max(element.column - 1, 0), element.row)
              "
              @dragover="(e) => dragOver(e)"
              @dragleave="(e) => dragLeave(e)"
            ></div>
            <div
              v-if="drag && dragItem && dragItem.id !== element.id"
              class="drop-target drop-after"
              @drop="(e) => dragDrop(e, element.column + 1, element.row)"
              @dragover="(e) => dragOver(e)"
              @dragleave="(e) => dragLeave(e)"
            ></div>
            <div
              v-if="drag && dragItem && dragItem.id !== element.id"
              class="drop-target drop-bottom"
              @drop="(e) => dragDrop(e, element.column, element.row + 1)"
              @dragover="(e) => dragOver(e)"
              @dragleave="(e) => dragLeave(e)"
            ></div>
          </div>
        </v-col>
        <v-col></v-col>
      </v-row>
    </v-table>

    <v-btn @click="editStory">Edit</v-btn>

    <v-menu>
      <template v-slot:activator="{ props }">
        <v-btn class="ml-4" color="success" v-bind="props">Add Item</v-btn>
      </template>
      <v-list>
        <v-list-item>
          <v-list-item-title>
            <v-btn
              @click="() => addItem(TYPE_ACTIONS_INITIATIVES)"
              block
              variant="elevated"
              >Actions/Initiatives</v-btn
            >
          </v-list-item-title>
        </v-list-item>
        <v-list-item>
          <v-list-item-title>
            <v-btn @click="() => addItem(TYPE_INFO)" block variant="elevated"
              >Info Page</v-btn
            >
          </v-list-item-title>
        </v-list-item>

        <v-list-item>
          <v-list-item-title>
            <v-btn
              @click="() => addItem(TYPE_INFOGRAPHIC)"
              block
              variant="elevated"
              >Infographic</v-btn
            >
          </v-list-item-title>
        </v-list-item>
        <!-- <v-list-item>
          <v-list-item-title>
            <v-btn @click="() => addItem(TYPE_HERO)" block variant="elevated"
              >Hero Page</v-btn
            >
          </v-list-item-title>
        </v-list-item> -->
        <v-list-item>
          <v-list-item-title>
            <v-btn
              @click="() => addItem(TYPE_QUESTION)"
              block
              variant="elevated"
              >Quiz question</v-btn
            >
          </v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>
    &nbsp;
    <v-btn @click="togglePublished">{{
      story.published ? 'Un-publish' : 'Publish'
    }}</v-btn>
    &nbsp;
    <v-btn @click="removeStory" color="warning">Delete</v-btn>
    <v-alert
      type="info"
      class="mt-8"
      v-if="
        story && story.items.length && !story.items[story.items.length - 1].info
      "
      >Use an info item as a the last page for the story. The info item is used
      to show the teaser.</v-alert
    >
  </div>
</template>
<style scoped>
.page-container {
  padding: 16px;
}
.table-container {
  padding: 0 16px;
}
.story-item {
  min-width: 140px;
  max-width: 140px;
  min-height: 200px;
  max-height: 200px;
  overflow: hidden;
  background-color: #e4e4e4;
  position: relative;
}

.story-item-type {
  position: absolute;
  bottom: 8px;
  left: 8px;
  color: gray;
}

.story-container {
  min-width: 100%;
  max-width: 100%;
  overflow-x: scroll;
  flex-wrap: nowrap;
  padding: 2em 48px;
}
.v-col {
  display: flex;
  width: auto;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
}
.draggable-container {
  position: relative;
  margin-bottom: 60px;
}

.drop-target {
  width: 80px;
  height: 80px;
  position: absolute;
  top: 0;
  left: 0;
  border: dashed 1px black;
  border-radius: 4px;
  border-color: rgb(0, 0, 0);
}

.drop-target.drag-active {
  border-color: rgba(46, 126, 246, 1);
  background-color: rgba(46, 126, 246, 0.5);
}
.drop-target.drop-before {
  left: auto;
  left: -44px;
  width: 40px;
  height: 80%;
  top: 10%;
  z-index: 2;
}
.drop-target.drop-after {
  left: auto;
  right: -44px;
  height: 80%;
  width: 40px;
  top: 10%;
  z-index: 2;
}
.drop-target.drop-bottom {
  left: 10%;
  width: 80%;
  height: 40px;
  top: auto;
  bottom: -44px;
  z-index: 2;
}

.remove-button {
  position: absolute;
  bottom: 8px;
  right: 8px;
  color: white;
  background-color: rgb(176, 176, 176);
}
</style>
